import { useMemo } from "react";

export const SERVICE_OPTIONS = [
  "Oil and filter change",
  "Tire rotation",
  "Tire repair",
  "Wheel alignment",
  "Brake inspection",
  "Front brake pads and rotors",
  "Rear brake pads and rotors",
  "Brake fluid flush",
  "Battery test and replacement",
  "Alternator test and replacement",
  "Starter test and replacement",
  "Coolant system inspection",
  "Radiator / coolant flush",
  "Thermostat replacement",
  "Water pump replacement",
  "Transmission fluid service",
  "Transmission fluid flush",
  "Power steering fluid flush",
  "Power steering pump replacement",
  "Engine air filter replacement",
  "Cabin air filter replacement",
  "Spark plug replacement",
  "Fuel-system cleaning",
  "Serpentine belt replacement",
  "Check-engine-light diagnosis",
  "Electrical-system diagnosis",
  "AC performance inspection",
  "State safety inspection",
  "Steering and suspension inspection",
  "Engine repair",
  "Transmission repair",
  "Other approved service",
];

const PRICE_OPTIONS = [
  ["none", "No price shown"],
  ["starting_at", "Starting at"],
  ["flat", "Flat price"],
  ["quote", "Quote required"],
];

const emptyRow = () => ({
  service: "",
  booking: "direct",
  priceType: "none",
  price: "",
  notes: "",
});

export function ServiceCatalog({ draft, setDraft }) {
  const rows = useMemo(
    () =>
      Array.isArray(draft.booking?.services) && draft.booking.services.length
        ? draft.booking.services
        : [emptyRow()],
    [draft.booking?.services],
  );
  const update = (index, patch) =>
    setDraft((current) => ({
      ...current,
      booking: {
        ...current.booking,
        services: rows.map((row, rowIndex) =>
          rowIndex === index ? { ...row, ...patch } : row,
        ),
      },
    }));
  const add = () =>
    setDraft((current) => ({
      ...current,
      booking: { ...current.booking, services: [...rows, emptyRow()] },
    }));
  const remove = (index) =>
    setDraft((current) => ({
      ...current,
      booking: {
        ...current.booking,
        services: rows.filter((_, rowIndex) => rowIndex !== index),
      },
    }));

  return (
    <section className="panel section catalog">
      <header>
        <p>Service catalog</p>
        <h2>What should RevEdge Auto know?</h2>
        <span>
          Choose the exact services you offer. Use flat or starting prices for
          routine maintenance, and “quote required” for complex repairs.
        </span>
      </header>
      <div className="catalog-head">
        <span>Service</span>
        <span>Booking handling</span>
        <span>Price rule</span>
        <span>Amount / range</span>
        <span>Notes</span>
        <span />
      </div>
      {rows.map((row, index) => (
        <div className="catalog-row" key={`${index}-${row.service}`}>
          <select
            value={row.service || ""}
            onChange={(event) => update(index, { service: event.target.value })}
          >
            <option value="">Select service…</option>
            {SERVICE_OPTIONS.map((service) => (
              <option key={service}>{service}</option>
            ))}
          </select>
          <select
            value={row.booking || "direct"}
            onChange={(event) => update(index, { booking: event.target.value })}
          >
            <option value="direct">Book directly</option>
            <option value="callback">Callback required</option>
          </select>
          <select
            value={row.priceType || "none"}
            onChange={(event) =>
              update(index, { priceType: event.target.value })
            }
          >
            {PRICE_OPTIONS.map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
          <input
            value={row.price || ""}
            placeholder="$79"
            disabled={row.priceType === "none" || row.priceType === "quote"}
            onChange={(event) => update(index, { price: event.target.value })}
          />
          <input
            value={row.notes || ""}
            placeholder="Optional note"
            onChange={(event) => update(index, { notes: event.target.value })}
          />
          <button
            type="button"
            className="icon-button"
            onClick={() => remove(index)}
            disabled={rows.length === 1}
            aria-label="Remove service"
          >
            ×
          </button>
        </div>
      ))}
      <button type="button" className="secondary add-row" onClick={add}>
        + Add service
      </button>
    </section>
  );
}

const SLOT_FIELDS = [
  ["leadTime", "Lead time", "24 hours"],
  ["sameDay", "Same-day limits", "Call shop for approval"],
  ["duration", "Appointment duration", "60 minutes"],
  ["buffers", "Buffers", "15 minutes"],
  ["blocked", "Blocked times", "Shop sets weekly"],
  ["horizon", "Booking horizon", "30 days"],
];

export function AppointmentRules({ draft, setDraft }) {
  const details =
    draft.booking?.slotRuleDetails ||
    Object.fromEntries(SLOT_FIELDS.map(([key, , value]) => [key, value]));
  const update = (key, value) => {
    const next = { ...details, [key]: value };
    setDraft((current) => ({
      ...current,
      booking: {
        ...current.booking,
        slotRuleDetails: next,
        slotRules: SLOT_FIELDS.map(
          ([field, label]) => `${label}: ${next[field] || "Not specified"}`,
        ).join("; "),
      },
    }));
  };
  return (
    <div className="slot-rules">
      <div className="slot-head">
        <span>Starting point</span>
        <span>Shop adjustment</span>
      </div>
      {SLOT_FIELDS.map(([key, label, value]) => (
        <label key={key}>
          <span>
            {label}
            <small>Suggested default: {value}</small>
          </span>
          <input
            value={details[key] || ""}
            onChange={(event) => update(key, event.target.value)}
          />
        </label>
      ))}
    </div>
  );
}
